import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { ChartSkeleton, skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC, ReactNode } from "react";

import {
  type DeltaTone,
  formatDeltaPercentage,
} from "../features/teammatePerformance/monthInReview/deltaPill.js";
import { TrendChart } from "../features/teammatePerformance/trendChart.js";

interface MetricAndTrendCardProps {
  readonly label: string;
  /** Shown when hovering the label. */
  readonly tooltip?: ReactNode;
  readonly currentValue: number | undefined;
  readonly previousValue: number | undefined;
  readonly trend: readonly (number | null)[] | undefined;
  /** Formats the headline number and each chart value. */
  readonly renderValue: (value: number) => string;
  readonly isCountLoading: boolean;
  readonly isDeltaLoading: boolean;
  readonly isTrendLoading: boolean;
  readonly comparisonLabel: string;
  readonly xAxisLabels?: { start: string; end: string };
  /**
   * When true, positive % changes are surfaced as a negative tone and vice
   * versa — use for "less-is-better" metrics like average call length.
   */
  readonly inverseTone?: boolean;
  /** Minimum y-axis range for the sparkline. */
  readonly minYRange?: number;
}

const invertTone = (tone: DeltaTone): DeltaTone =>
  tone === "positive"
    ? "negative"
    : tone === "negative"
      ? "positive"
      : "neutral";

export const MetricAndTrendCard: FC<MetricAndTrendCardProps> = ({
  label,
  tooltip,
  currentValue,
  previousValue,
  trend,
  renderValue,
  isCountLoading,
  isDeltaLoading,
  isTrendLoading,
  comparisonLabel,
  xAxisLabels,
  inverseTone = false,
  minYRange = 5,
}) => {
  const { delta, tone: rawTone } = formatDeltaPercentage(
    currentValue,
    previousValue,
  );
  const tone = inverseTone ? invertTone(rawTone) : rawTone;

  return (
    <div className="[container-type:inline-size] rounded-lg border border-border-light bg-white p-4">
      <div className="grid grid-cols-1 gap-4 [@container(min-width:20rem)]:grid-cols-[auto_1fr] [@container(min-width:20rem)]:gap-6">
        <div className="flex flex-col justify-center">
          <Label size={Size.SMALL}>
            {tooltip ? (
              <TooltipTrigger>
                <TooltipHoverTarget>{label}</TooltipHoverTarget>
                <SimpleTooltip>{tooltip}</SimpleTooltip>
              </TooltipTrigger>
            ) : (
              label
            )}
          </Label>
          <div className="mt-2 flex items-end gap-2">
            {isCountLoading ? (
              <div
                className={skeletonStyle({ class: "h-9 w-20 rounded-sm" })}
              />
            ) : (
              <div className="text-3xl font-semibold text-foreground">
                {currentValue !== undefined ? renderValue(currentValue) : "—"}
              </div>
            )}
            {isDeltaLoading ? (
              <div
                className={skeletonStyle({ class: "mb-1 h-4 w-24 rounded-sm" })}
              />
            ) : trend ? (
              <p className="mb-0.5 text-xs text-muted-foreground">
                <span
                  className={
                    tone === "positive"
                      ? "mr-1 text-success"
                      : tone === "negative"
                        ? "mr-1 text-red-500"
                        : "mr-1 text-gray-400"
                  }
                >
                  {delta}
                </span>
                vs {comparisonLabel}
              </p>
            ) : null}
          </div>
        </div>

        <div className="min-h-0 min-w-0 [@container(max-width:20rem)]:h-14">
          {isTrendLoading ? (
            <div className="h-full">
              <ChartSkeleton />
            </div>
          ) : trend ? (
            <TrendChart
              className="h-full w-full"
              minYRange={minYRange}
              showsXAxisLabels
              showsYAxisLabels
              lines={[{ key: "metric", data: trend }]}
              renderValue={renderValue}
              getColorClassName={() => "text-muted-foreground"}
              xAxisLabels={xAxisLabels}
            />
          ) : (
            <div className="flex h-full items-center text-sm text-muted-foreground">
              No activity yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
